import { useApolloClient } from "@apollo/client";
import { MenuItem, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import React from "react";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { asyncForEach } from "src/services/asyncHelpers";
import { maybePluralize } from "src/services/format";
import { OfferError } from "../errors";
import {
  FETCH_OFFER_WAITLIST_STATUS,
  GET_ASSIGNED_FORMS_BY_FORM_ID,
} from "../graphql/queries";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { canRevokeOffers } from "../validations/revokeOffers";
import { trackFormAction, trackFormActionError } from "./trackFormAction";

const REVOKE_BULK_FORM_SCHOOL_OFFER_MAX_INPUT_SIZE = 500;

interface Props {
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
  onRefetch?: () => void;
  menuLabel?: string;
  isBulkAction?: boolean;
}

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "Revoking this offer will prevent or undo the family's acceptance.",
    "Revoking these offers will prevent or undo the families' acceptance."
  );

const confirmButtonLabel = (count: number) =>
  maybePluralize(count, "Yes, revoke offer", "Yes, revoke offers");

export const RevokeOffer = ({
  selectedFormSchoolStatuses,
  selectedFormSchools,
  onRefetch,
  menuLabel = "Revoke offers",
  isBulkAction = false,
}: Props) => {
  const client = useApolloClient();
  const queryClient = useQueryClient();

  const { mutateAsync: revokeBulkFormSchoolOffer } = FSQ.useBulkRevokeOffer();

  const toast = useAvelaToast();

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: null,
    body: null,
    cancelButton: {
      label: "Cancel",
    },
    translate: true,
  });

  const handleOnClick = React.useCallback(
    async (evt: React.MouseEvent) => {
      const schoolForms = selectedFormSchoolStatuses;
      const count = schoolForms.length;
      const trackArgs = {
        actionType: "REVOKE_OFFER" as const,
        bulk: isBulkAction,
        count,
        async: false,
      };

      const offerNoun = maybePluralize(count, "offer");

      trackFormAction({ verb: "Clicked", ...trackArgs });

      if (
        await confirm({
          header: <Text>{`Revoke ${count} ${offerNoun}?`}</Text>,
          body: <Text>{confirmationBodyText(count)}</Text>,
          confirmButton: {
            label: confirmButtonLabel(count),
            colorScheme: "blue",
          },
        })
      ) {
        trackFormAction({ verb: "Confirmed", ...trackArgs });

        const loadingToastId = toast({
          title: `Working hard`,
          description: `Revoking ${count} ${offerNoun}...`,
          status: "loading",
          duration: null,
        });

        try {
          const offerIds = schoolForms.map((result) => {
            if (!result.offers[0]?.id) {
              throw new OfferError("One of the selected form(s) has no offer");
            }

            return { offer_id: result.offers[0].id };
          });

          await asyncForEach(
            _.chunk(offerIds, REVOKE_BULK_FORM_SCHOOL_OFFER_MAX_INPUT_SIZE),
            (offerChunk) =>
              revokeBulkFormSchoolOffer({
                body: offerChunk,
              })
          );

          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: [FSQ.useGetCapacitiesBySchoolAndEnrollmentPeriodKey],
              exact: false,
            }),
            queryClient.invalidateQueries({
              queryKey: [FSQ.useGetCapacitySummaryByEnrollmentPeriodKey],
              exact: false,
            }),
            client.refetchQueries({
              include: [
                FETCH_OFFER_WAITLIST_STATUS,
                GET_ASSIGNED_FORMS_BY_FORM_ID,
              ],
            }),
          ]);
          if (onRefetch) onRefetch();

          toast.close(loadingToastId);
          toast({
            description: `${count} ${offerNoun} revoked`,
            status: "info",
          });
          trackFormAction({ verb: "Completed", ...trackArgs });
        } catch (err: any) {
          trackFormActionError(err, trackArgs);
          toast.close(loadingToastId);
          let message = "Check your network and try again.";

          if (err instanceof OfferError) {
            message = err.message;
          }

          toast.error({
            title: "Oops! Something went wrong!",
            description: message,
          });
          console.error(err);
        }
      } else {
        trackFormAction({ verb: "Canceled", ...trackArgs });
      }
    },
    [
      client,
      confirm,
      isBulkAction,
      onRefetch,
      queryClient,
      revokeBulkFormSchoolOffer,
      selectedFormSchoolStatuses,
      toast,
    ]
  );

  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !canRevokeOffers(selectedFormSchoolStatuses, selectedFormSchools)
        }
      >
        {menuLabel}
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
